// src/firebase.js
import { initializeApp } from "firebase/app";
import {
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";

// --- MASTER REGISTRY CONFIG (taxcrm-master-registry) ---
const MASTER_REGISTRY_CONFIG = {
    apiKey: "AIzaSyBpRokc-bXzNFdX6LlSRzY71BeMeZzKlYQ",
    projectId: "taxcrm-master-registry"
};

// 1. Default Fallback Configuration (.env / nvs-app-developer)
const defaultFirebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

function getSubdomainCode() {
    if (typeof window === "undefined") return null;
    const host = window.location.hostname.toLowerCase();
    if (
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.endsWith(".web.app") ||
        host.endsWith(".firebaseapp.com")
    ) {
        return null;
    }
    const parts = host.split(".");
    if (parts.length >= 3) {
        const sub = parts[0].toUpperCase();
        if (sub && sub !== "WWW" && sub !== "MYCLIENTDRIVE") {
            return sub;
        }
    }
    return null;
}

function resolveInitialFirebaseConfig() {
    const subCode = getSubdomainCode();
    if (!subCode || typeof window === "undefined") {
        return defaultFirebaseConfig;
    }

    const cacheKey = `mcd_portal_tenant_config_${subCode}`;
    try {
        const cachedRaw = window.localStorage.getItem(cacheKey);
        if (cachedRaw) {
            const parsed = JSON.parse(cachedRaw);
            if (parsed && parsed.apiKey && parsed.projectId) {
                return parsed;
            }
        }
    } catch (e) {
        // Fallback to default while background lookup completes
    }

    // Background lookup in taxcrm-master-registry for the subdomain
    const url = `https://firestore.googleapis.com/v1/projects/${MASTER_REGISTRY_CONFIG.projectId}/databases/(default)/documents/firm_configs/${subCode}?key=${MASTER_REGISTRY_CONFIG.apiKey}`;
    fetch(url)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
            if (!data || !data.fields?.firebaseConfig?.mapValue?.fields) return;
            const fb = data.fields.firebaseConfig.mapValue.fields;
            const resolvedConfig = {
                apiKey: fb.apiKey?.stringValue || "",
                authDomain: fb.authDomain?.stringValue || "",
                projectId: fb.projectId?.stringValue || "",
                storageBucket: fb.storageBucket?.stringValue || "",
                messagingSenderId: (fb.messagingSenderId || fb.messagingSenderID)?.stringValue || "",
                appId: (fb.appId || fb.appID)?.stringValue || ""
            };
            if (resolvedConfig.apiKey && resolvedConfig.projectId) {
                window.localStorage.setItem(cacheKey, JSON.stringify(resolvedConfig));
                if (resolvedConfig.projectId !== defaultFirebaseConfig.projectId) {
                    window.location.reload();
                }
            }
        })
        .catch(() => { });

    return defaultFirebaseConfig;
}

const firebaseConfig = resolveInitialFirebaseConfig();

// 2. Initialize Firebase
const app = initializeApp(firebaseConfig);

// 3. Initialize Firestore with Offline Persistence Settings
const db = initializeFirestore(app, {
    localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
    })
});

// 4. Initialize other services
const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app);

export { db, auth, storage, functions, firebaseConfig };